<div class="space-y-6">
  <!-- Header & Actions -->
  <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
    <div>
      <h3 class="text-xl font-bold text-base-content flex items-center gap-2">
        <i class="fa-solid fa-clipboard-question text-primary"></i>
        Class Quizzes
      </h3>
      <p class="text-sm text-base-content/60">
        Create assessments, schedule access times, manage question banks, and review results
      </p>
    </div>

    @if (editModeEnabled) {
      <div class="flex items-center gap-2">
        <!-- Question Bank Manager Modal Trigger -->
        <black-ui-modal>
          <button
            *blackModalTrigger="let toggle = toggle"
            (click)="toggle(); fetchQuestionBank()"
            class="daisy-btn daisy-btn-outline daisy-btn-sm sm:daisy-btn-md gap-2"
          >
            <i class="fa-solid fa-box-archive"></i>
            Question Bank ({{ questionBank.length }})
          </button>

          <div
            *blackModalContent="let closeBank = close"
            class="reveal-modal"
            (click)="closeBank()"
          >
            <div
              class="reveal-modal-body rounded-2xl bg-base-100 max-w-3xl w-full p-6 shadow-2xl overflow-y-auto max-h-[85vh]"
              (click)="$event.stopPropagation()"
            >
              <div class="flex items-center justify-between pb-4 border-b border-base-200">
                <h3 class="text-lg font-bold text-base-content flex items-center gap-2">
                  <i class="fa-solid fa-box-archive text-primary"></i>
                  Question Bank
                </h3>
                <button
                  (click)="closeBank()"
                  class="daisy-btn daisy-btn-ghost daisy-btn-circle daisy-btn-sm"
                >
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>

              <div class="py-4 space-y-4">
                <input
                  [(ngModel)]="bankSearchQuery"
                  type="text"
                  placeholder="Search saved questions..."
                  class="daisy-input daisy-input-bordered daisy-input-sm w-full"
                />

                @if (loadingQuestionBank) {
                  <div class="flex justify-center py-8">
                    <span class="daisy-loading daisy-loading-spinner text-primary"></span>
                  </div>
                } @else if (filteredQuestionBank.length === 0) {
                  <div class="daisy-alert">
                    <span>No questions saved in bank yet.</span>
                  </div>
                } @else {
                  <div class="space-y-3">
                    @for (item of filteredQuestionBank; track item._id) {
                      <div class="p-3 bg-base-200/50 rounded-xl border border-base-300 flex items-start justify-between gap-3">
                        <div class="space-y-1">
                          <p class="font-medium text-sm text-base-content">{{ item.question }}</p>
                          <div class="flex items-center gap-2 text-xs text-base-content/60">
                            <span>{{ item.options?.length || 0 }} options</span>
                            <span>•</span>
                            <span>{{ item.marks || 1 }} mark(s)</span>
                          </div>
                        </div>
                        <button
                          (click)="deleteFromBank(item._id)"
                          class="daisy-btn daisy-btn-ghost daisy-btn-xs text-error"
                        >
                          <i class="fa-solid fa-trash"></i>
                        </button>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          </div>
        </black-ui-modal>

        <!-- Create Quiz Modal Trigger -->
        <black-ui-modal>
          <button
            *blackModalTrigger="let toggle = toggle"
            (click)="toggle(); resetQuizForm()"
            class="daisy-btn daisy-btn-primary daisy-btn-sm sm:daisy-btn-md gap-2"
          >
            <i class="fa-solid fa-plus"></i>
            Create Quiz
          </button>

          <div
            *blackModalContent="let close = close"
            class="reveal-modal"
            (click)="close()"
          >
            <div
              class="reveal-modal-body rounded-2xl bg-base-100 max-w-3xl w-full p-6 shadow-2xl overflow-y-auto max-h-[90vh]"
              (click)="$event.stopPropagation()"
            >
              <!-- Modal Header -->
              <div class="flex items-center justify-between pb-4 border-b border-base-200">
                <h3 class="text-lg font-bold text-base-content flex items-center gap-2">
                  <i class="fa-solid fa-feather text-primary"></i>
                  Create Quiz
                </h3>
                <button
                  (click)="close()"
                  class="daisy-btn daisy-btn-ghost daisy-btn-circle daisy-btn-sm"
                >
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>

              <div class="py-4 space-y-6">
                <!-- Basic Info -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="md:col-span-2">
                    <label class="daisy-label">
                      <span class="daisy-label-text font-semibold">Quiz Title *</span>
                    </label>
                    <input
                      [(ngModel)]="quizTitle"
                      type="text"
                      placeholder="e.g. Midterm Evaluation Quiz"
                      class="daisy-input daisy-input-bordered w-full"
                      required
                    />
                  </div>

                  <div class="md:col-span-2">
                    <label class="daisy-label">
                      <span class="daisy-label-text">Description / Instructions</span>
                    </label>
                    <textarea
                      [(ngModel)]="quizDescription"
                      rows="2"
                      placeholder="Provide guidelines for students..."
                      class="daisy-textarea daisy-textarea-bordered w-full"
                    ></textarea>
                  </div>

                  <div>
                    <label class="daisy-label">
                      <span class="daisy-label-text">Duration (Minutes)</span>
                    </label>
                    <input
                      [(ngModel)]="quizDuration"
                      type="number"
                      min="0"
                      placeholder="0 for untimed"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                  </div>

                  <div>
                    <label class="daisy-label">
                      <span class="daisy-label-text">Passing Marks</span>
                    </label>
                    <input
                      [(ngModel)]="quizPassingMarks"
                      type="number"
                      min="0"
                      placeholder="e.g. 5"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                  </div>

                  <div>
                    <label class="daisy-label">
                      <span class="daisy-label-text">Max Attempts</span>
                    </label>
                    <input
                      [(ngModel)]="quizMaxAttempts"
                      type="number"
                      min="1"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                  </div>

                  <div>
                    <label class="daisy-label">
                      <span class="daisy-label-text">Attach to Session (Optional)</span>
                    </label>
                    <black-session-picker
                      [liveClassId]="selectedLiveClassId"
                      (sessionSelected)="onSessionSelected($event)"
                    ></black-session-picker>
                  </div>

                  <!-- Scheduled Access Window -->
                  <div>
                    <label class="daisy-label">
                      <span class="daisy-label-text font-medium flex items-center gap-1.5">
                        <i class="fa-solid fa-calendar-days text-info"></i>
                        Available From (Optional)
                      </span>
                    </label>
                    <input
                      [(ngModel)]="quizAvailableFrom"
                      type="datetime-local"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                  </div>

                  <div>
                    <label class="daisy-label">
                      <span class="daisy-label-text font-medium flex items-center gap-1.5">
                        <i class="fa-solid fa-calendar-xmark text-warning"></i>
                        Available Until (Optional)
                      </span>
                    </label>
                    <input
                      [(ngModel)]="quizAvailableTo"
                      type="datetime-local"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                  </div>
                </div>

                <!-- Question List Summary & Import from Bank Button -->
                <div class="flex items-center justify-between pt-2">
                  <h4 class="font-bold text-sm text-base-content">
                    Questions ({{ quizQuestions.length }})
                  </h4>

                  <!-- Import from Question Bank Modal -->
                  <black-ui-modal>
                    <button
                      *blackModalTrigger="let toggleBank = toggle"
                      (click)="toggleBank(); fetchQuestionBank()"
                      class="daisy-btn daisy-btn-outline daisy-btn-xs text-primary gap-1"
                    >
                      <i class="fa-solid fa-file-import"></i>
                      Import from Question Bank
                    </button>

                    <div
                      *blackModalContent="let closeImport = close"
                      class="reveal-modal"
                      (click)="closeImport()"
                    >
                      <div
                        class="reveal-modal-body rounded-2xl bg-base-100 max-w-2xl w-full p-6 shadow-2xl overflow-y-auto max-h-[80vh]"
                        (click)="$event.stopPropagation()"
                      >
                        <div class="flex items-center justify-between pb-4 border-b border-base-200">
                          <h3 class="text-base font-bold text-base-content">
                            Select Questions to Import
                          </h3>
                          <button
                            (click)="closeImport()"
                            class="daisy-btn daisy-btn-ghost daisy-btn-circle daisy-btn-xs"
                          >
                            <i class="fa-solid fa-xmark"></i>
                          </button>
                        </div>

                        <div class="py-4 space-y-3">
                          @if (questionBank.length === 0) {
                            <p class="text-sm text-base-content/60">No saved questions in bank.</p>
                          } @else {
                            @for (item of questionBank; track item._id) {
                              <label class="flex items-start gap-3 p-3 rounded-lg border border-base-300 hover:bg-base-200/50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  [checked]="selectedBankQuestionIds.has(item._id)"
                                  (change)="toggleBankQuestionSelection(item._id)"
                                  class="daisy-checkbox daisy-checkbox-primary daisy-checkbox-sm mt-0.5"
                                />
                                <div>
                                  <div class="text-sm font-medium text-base-content">{{ item.question }}</div>
                                  <div class="text-xs text-base-content/60">{{ item.options?.length || 0 }} options • {{ item.marks || 1 }} mark(s)</div>
                                </div>
                              </label>
                            }
                          }
                        </div>

                        <div class="flex justify-end gap-2 pt-3 border-t border-base-200">
                          <button (click)="closeImport()" class="daisy-btn daisy-btn-ghost daisy-btn-sm">Cancel</button>
                          <button
                            (click)="importSelectedQuestions(closeImport)"
                            class="daisy-btn daisy-btn-primary daisy-btn-sm"
                            [disabled]="selectedBankQuestionIds.size === 0"
                          >
                            Import Selected ({{ selectedBankQuestionIds.size }})
                          </button>
                        </div>
                      </div>
                    </div>
                  </black-ui-modal>
                </div>

                @if (quizQuestions.length > 0) {
                  <div class="border border-base-300 rounded-xl p-4 bg-base-200/50 space-y-2">
                    @for (q of quizQuestions; track q.id; let i = $index) {
                      <div class="flex items-center justify-between p-3 bg-base-100 rounded-lg border border-base-300">
                        <div class="flex items-center gap-3">
                          <span class="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">
                            {{ i + 1 }}
                          </span>
                          <span class="font-medium text-sm text-base-content">{{ q.question }}</span>
                        </div>
                        <div class="flex items-center gap-2">
                          <span class="text-xs text-base-content/60">{{ q.marks }} mark(s)</span>
                          <button
                            (click)="removeQuestion(i)"
                            class="daisy-btn daisy-btn-ghost daisy-btn-xs text-error"
                          >
                            <i class="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    }
                  </div>
                }

                <!-- Add Question Form -->
                <div class="border-2 border-dashed border-base-300 rounded-xl p-4 space-y-4">
                  <h4 class="font-bold text-sm text-base-content flex items-center gap-2">
                    <i class="fa-solid fa-plus-circle text-primary"></i>
                    Add Question
                  </h4>

                  <div>
                    <input
                      [(ngModel)]="currentQuestionText"
                      type="text"
                      placeholder="Enter question prompt..."
                      class="daisy-input daisy-input-bordered w-full"
                    />
                  </div>

                  <div class="space-y-2">
                    <label class="text-xs font-semibold text-base-content/70">
                      Options (Select the radio button for the correct answer)
                    </label>
                    @for (opt of currentOptions; track opt.id; let optIdx = $index) {
                      <div class="flex items-center gap-2">
                        <input
                          type="radio"
                          name="correctOptionRadio"
                          [checked]="opt.isCorrect"
                          (change)="setCorrectOption(optIdx)"
                          class="daisy-radio daisy-radio-primary daisy-radio-sm"
                        />
                        <input
                          [(ngModel)]="opt.text"
                          type="text"
                          [placeholder]="'Option ' + (optIdx + 1)"
                          class="daisy-input daisy-input-bordered daisy-input-sm flex-1"
                        />
                        <button
                          (click)="removeOption(optIdx)"
                          class="daisy-btn daisy-btn-ghost daisy-btn-xs text-error"
                          [disabled]="currentOptions.length <= 2"
                        >
                          <i class="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                    }
                    <button
                      (click)="addOption()"
                      class="daisy-btn daisy-btn-ghost daisy-btn-xs text-primary gap-1 mt-1"
                    >
                      <i class="fa-solid fa-plus"></i> Add Option
                    </button>
                  </div>

                  <div class="flex flex-wrap items-center justify-between gap-2 pt-2">
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        [(ngModel)]="saveCurrentToBank"
                        class="daisy-checkbox daisy-checkbox-primary daisy-checkbox-xs"
                      />
                      <span class="text-xs text-base-content/70">Save to Question Bank</span>
                    </label>

                    <button
                      (click)="addQuestionToQuiz()"
                      class="daisy-btn daisy-btn-secondary daisy-btn-sm gap-2"
                    >
                      <i class="fa-solid fa-check"></i>
                      Add Question to Quiz
                    </button>
                  </div>
                </div>
              </div>

              <!-- Modal Footer: Draft vs Publish -->
              <div class="flex items-center justify-between pt-4 border-t border-base-200">
                <button (click)="close()" class="daisy-btn daisy-btn-ghost">
                  Cancel
                </button>

                <div class="flex items-center gap-2">
                  <button
                    (click)="saveQuiz(false, close)"
                    class="daisy-btn daisy-btn-outline"
                    [disabled]="quizQuestions.length === 0 || !quizTitle.trim()"
                  >
                    <i class="fa-solid fa-floppy-disk"></i>
                    Save as Draft
                  </button>
                  <button
                    (click)="saveQuiz(true, close)"
                    class="daisy-btn daisy-btn-primary"
                    [disabled]="quizQuestions.length === 0 || !quizTitle.trim()"
                  >
                    <i class="fa-solid fa-upload"></i>
                    Publish Quiz
                  </button>
                </div>
              </div>
            </div>
          </div>
        </black-ui-modal>
      </div>
    }
  </div>

  <!-- Quiz Cards List -->
  @if (loading) {
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="daisy-skeleton h-44 w-full rounded-2xl"></div>
      <div class="daisy-skeleton h-44 w-full rounded-2xl"></div>
    </div>
  } @else if (quizzes.length === 0) {
    <div class="daisy-card daisy-card-bordered bg-base-100 p-8 text-center">
      <div class="w-16 h-16 rounded-full bg-base-200 flex items-center justify-center mx-auto mb-4 text-base-content/40">
        <i class="fa-solid fa-clipboard-question text-3xl"></i>
      </div>
      <h4 class="text-lg font-bold text-base-content mb-1">No Quizzes Created</h4>
      <p class="text-sm text-base-content/60 max-w-sm mx-auto">
        Create drafts or published quizzes to test student comprehension.
      </p>
    </div>
  } @else {
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      @for (quiz of quizzes; track quiz._id) {
        <div class="daisy-card daisy-card-bordered bg-base-100 shadow-sm hover:shadow-md transition-shadow">
          <div class="daisy-card-body p-5 flex flex-col justify-between">
            <div>
              <div class="flex items-start justify-between gap-2 mb-2">
                <h4 class="font-bold text-base text-base-content line-clamp-1">
                  {{ quiz.title }}
                </h4>
                @if (quiz.isPublished) {
                  <span class="daisy-badge daisy-badge-success daisy-badge-sm">Published</span>
                } @else {
                  <span class="daisy-badge daisy-badge-ghost daisy-badge-sm">Draft</span>
                }
              </div>

              @if (quiz.description) {
                <p class="text-xs text-base-content/60 line-clamp-2 mb-3">
                  {{ quiz.description }}
                </p>
              }

              <!-- Badges & Timing -->
              <div class="flex flex-wrap gap-2 text-xs text-base-content/70 my-2">
                <span class="daisy-badge daisy-badge-outline daisy-badge-sm gap-1">
                  <i class="fa-solid fa-list-check text-primary"></i>
                  {{ quiz.questions.length }} Questions
                </span>
                <span class="daisy-badge daisy-badge-outline daisy-badge-sm gap-1">
                  <i class="fa-solid fa-star text-warning"></i>
                  {{ quiz.totalMarks }} Marks
                </span>
                @if (quiz.durationMinutes) {
                  <span class="daisy-badge daisy-badge-outline daisy-badge-sm gap-1">
                    <i class="fa-solid fa-clock text-info"></i>
                    {{ quiz.durationMinutes }}m
                  </span>
                }
              </div>

              <!-- Scheduled Access Window Display -->
              @if (quiz.availableFrom || quiz.availableTo) {
                <div class="p-2 bg-base-200/60 rounded-lg text-xs space-y-1 my-2">
                  @if (quiz.availableFrom) {
                    <div class="text-base-content/70 flex items-center gap-1.5">
                      <i class="fa-solid fa-calendar-check text-success"></i>
                      <span>From: {{ quiz.availableFrom | date: 'short' }}</span>
                    </div>
                  }
                  @if (quiz.availableTo) {
                    <div class="text-base-content/70 flex items-center gap-1.5">
                      <i class="fa-solid fa-calendar-xmark text-error"></i>
                      <span>Until: {{ quiz.availableTo | date: 'short' }}</span>
                    </div>
                  }
                </div>
              }
            </div>

            <div class="flex items-center justify-between pt-3 border-t border-base-200 mt-2">
              <black-ui-modal>
                <button
                  *blackModalTrigger="let toggle = toggle"
                  (click)="toggle(); viewSubmissions(quiz)"
                  class="daisy-btn daisy-btn-sm daisy-btn-ghost gap-1 text-primary text-xs"
                >
                  <i class="fa-solid fa-chart-simple"></i>
                  Results
                </button>

                <div
                  *blackModalContent="let closeSub = close"
                  class="reveal-modal"
                  (click)="closeSub()"
                >
                  <div
                    class="reveal-modal-body rounded-2xl bg-base-100 max-w-4xl w-full p-6 shadow-2xl overflow-y-auto max-h-[85vh]"
                    (click)="$event.stopPropagation()"
                  >
                    <div class="flex items-center justify-between pb-4 border-b border-base-200">
                      <div>
                        <h3 class="text-lg font-bold text-base-content flex items-center gap-2">
                          <i class="fa-solid fa-users text-primary"></i>
                          Submissions: {{ selectedQuizForSubmissions?.title }}
                        </h3>
                        <p class="text-xs text-base-content/60 mt-0.5">
                          Passing Mark: {{ selectedQuizForSubmissions?.passingMarks }} / {{ selectedQuizForSubmissions?.totalMarks }}
                        </p>
                      </div>
                      <button
                        (click)="closeSub()"
                        class="daisy-btn daisy-btn-ghost daisy-btn-circle daisy-btn-sm"
                      >
                        <i class="fa-solid fa-xmark"></i>
                      </button>
                    </div>

                    <div class="py-4">
                      @if (loadingSubmissions) {
                        <div class="flex justify-center py-8">
                          <span class="daisy-loading daisy-loading-spinner text-primary"></span>
                        </div>
                      } @else if (submissions.length === 0) {
                        <div class="daisy-alert daisy-alert-info">
                          <span>No submissions recorded yet.</span>
                        </div>
                      } @else {
                        <div class="overflow-x-auto">
                          <table class="daisy-table daisy-table-zebra w-full">
                            <thead>
                              <tr>
                                <th>Student</th>
                                <th>Attempt</th>
                                <th>Score</th>
                                <th>Status</th>
                                <th>Submitted At</th>
                              </tr>
                            </thead>
                            <tbody>
                              @for (sub of submissions; track sub._id) {
                                <tr>
                                  <td>
                                    <div class="flex items-center gap-3">
                                      <div class="daisy-avatar daisy-placeholder">
                                        <div class="bg-neutral text-neutral-content rounded-full w-8">
                                          <span class="text-xs">{{ sub.user?.firstName?.[0] || 'S' }}</span>
                                        </div>
                                      </div>
                                      <div>
                                        <div class="font-bold">{{ sub.user?.firstName }} {{ sub.user?.lastName }}</div>
                                        <div class="text-xs opacity-50">{{ sub.user?.email }}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td>#{{ sub.attemptNumber }}</td>
                                  <td>
                                    <span class="font-semibold">{{ sub.totalScore }}</span> / {{ selectedQuizForSubmissions?.totalMarks }}
                                  </td>
                                  <td>
                                    @if (sub.passed) {
                                      <span class="daisy-badge daisy-badge-success daisy-badge-sm">Passed</span>
                                    } @else {
                                      <span class="daisy-badge daisy-badge-error daisy-badge-sm">Failed</span>
                                    }
                                  </td>
                                  <td class="text-xs text-base-content/70">
                                    {{ sub.submittedAt | date: 'short' }}
                                  </td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </black-ui-modal>

              <div class="flex items-center gap-1">
                <button
                  (click)="togglePublish(quiz)"
                  class="daisy-btn daisy-btn-ghost daisy-btn-xs"
                  [title]="quiz.isPublished ? 'Move to Draft' : 'Publish'"
                >
                  <i [class]="quiz.isPublished ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                </button>
                <button
                  (click)="deleteQuiz(quiz._id)"
                  class="daisy-btn daisy-btn-ghost daisy-btn-xs text-error"
                  title="Delete"
                >
                  <i class="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  }
</div>
